import {
  deleteCategory as deleteCategoryApi,
  getCategories,
  getCategoryByID,
  postCategory,
  putCategory,
} from '../../api/categories'
import { ResolveCreateProductAction, ResolveUpdateProductAction } from '../actions/products'
import { CREATE_PRODUCT, UPDATE_PRODUCT } from '../constants/ActionTypes'
import { createModelStore, ModelStateKey, GenericModelState, GenericResponse } from '@scrubtech/redux-model-factory'
import { PaginationInfo, Category } from '@scrubtech/models'
import { RootState } from '../types'

// Create the store module
const storeModule = createModelStore<Category>({
  actionTypePrefix: 'CATEGORIES',
  getEntityID:     category => category.id,
  stateKey:       'categories' as ModelStateKey<Category>,
  entityName:     'Category',
  service:        {
    fetchAll: async (params): Promise<{ items: Category[], paginationInfo: PaginationInfo }> => {
      return await getCategories(params.page, params.limit, params.search)
    },
    fetchByID: async (id): Promise<Category> => {
      return await getCategoryByID(id)
    },
    create: async (data: Partial<Category>): Promise<Category> => {
      if (!data.name) {
        throw new Error('Category name is required')
      }
      return await postCategory(data.name)
    },
    update: async (id: number, data: Partial<Category>): Promise<Category> => {
      if (!data.name) {
        throw new Error('Category name is required')
      }
      return await putCategory(id, data.name)
    },
    delete: async (id: number): Promise<Category> => {
      return await deleteCategoryApi(id)
    },
  },
  navigationConfig: {
    getPostCreationUrl: (category) => `/categories/${category.id}`,
    getPostDeletionUrl: () => '/categories'
  },
  customActionHandlers: {
    [CREATE_PRODUCT]: (state: GenericModelState<Category>, action: any) => {
      if (!action.success) return state;

      let { payload } = action as ResolveCreateProductAction
      const { product } = payload

      // Add product to category
      let category = {
        ...state.byID[product.category!.id],
      } as Category
      category.products?.push(product)

      // Replace item in byID
      let byID = {
        ...state.byID,
      }
      byID[category.id] = category

      return {
        ...state,
        byID
      };
    },
    [UPDATE_PRODUCT]: (state: GenericModelState<Category>, action: any) => {
      if (!action.success) return state;

      let { payload } = action as ResolveUpdateProductAction
      const { product: editedProduct } = payload

      // Check if the product's category has changed
      let category = { ...state.byID[editedProduct.category!.id] } as Category
      const oldIndex = category.products?.findIndex(product => editedProduct.id == product.id)
      if (oldIndex !== -1) {
        // Category has not changed. Just update product in category
        category.products = category.products?.map(p => {
          if (p.id == editedProduct.id) {
            return editedProduct
          }
          return p
        }) || null
        let byID = {
          ...state.byID,
        }
        byID[category.id] = category

        return {
          ...state,
          byID,
        }
      }

      // Category has changed
      let byID = {
        ...state.byID,
      }

      // Remove from old category
      for (const oldCategoryID in byID) {
        // Copy, lest we modify in memory
        let oldCategory = {
          ...byID[oldCategoryID],
          products: byID[oldCategoryID].products?.filter(product => product.id !== editedProduct.id),
        } as Category
        byID[oldCategory.id] = oldCategory
      }

      // Add category
      category.products?.push(editedProduct)
      byID[category.id] = category

      return {
        ...state,
        byID,
      }
    }
  },
});

// Extract and export the reducer, actions, and selectors
const {
  reducer,
  actions,
  selectors,
  actionTypes,
} = storeModule

// Export everything
export {
  // Actions
  actions as categoriesActions,
  
  // Selectors
  selectors as categoriesSelectors,
  
  // Action Types
  actionTypes as categoriesActionTypes,
  
  // Reducer
  reducer as categoriesReducer,
};

// Export types
export type { Category };

// Export query creator
export const AllCategoriesQuery = {
  page: 1,
  limit: 9001,
  search: ''
}
